import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '@/lib/stripe';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { getErrorMessage } from '@/lib/utils';

interface CreditPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clientSecret: string | null;
  pack: {
    name: string;
    credits: number;
    price: number;
  } | null;
}

const CheckoutForm: React.FC<{
  onSuccess: () => void;
  onCancel: () => void;
  pack: { name: string; credits: number; price: number };
}> = ({ onSuccess, onCancel, pack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setErrorMessage('Stripe is not ready. Please try again.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Submit the payment elements to ensure they're ready
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || 'Payment validation failed');
        setIsProcessing(false);
        return;
      }

      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/billing?payment=success`,
        },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message || 'Payment failed');
        setIsProcessing(false);
      } else {
        // Payment succeeded
        onSuccess();
      }
    } catch (err: unknown) {
      setErrorMessage(getErrorMessage(err) || 'An unexpected error occurred');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Pack Summary */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-600">Purchasing</p>
            <p className="text-lg font-semibold text-gray-900">{pack.name}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">{pack.credits.toLocaleString()} credits</p>
            <p className="text-2xl font-bold text-primary">${pack.price}</p>
          </div>
        </div>
      </div>

      {/* Payment Element */}
      <div>
        <PaymentElement 
          onReady={() => setIsReady(true)}
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {!isReady && !isProcessing && (
        <div className="flex items-center justify-center py-4">
          <LoadingSpinner size="sm" />
          <span className="ml-2 text-sm text-gray-600">Loading payment form...</span>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="px-4 py-3 bg-error-50 border border-error-200 rounded-lg">
          <p className="text-sm text-error-600">{errorMessage}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || !isReady || isProcessing}
          className="px-6 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          {isProcessing && <LoadingSpinner size="sm" />}
          {isProcessing ? 'Processing...' : !isReady ? 'Loading...' : `Pay $${pack.price}`}
        </button>
      </div>
    </form>
  );
};

export const CreditPurchaseModal: React.FC<CreditPurchaseModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  clientSecret,
  pack,
}) => {
  if (!isOpen || !clientSecret || !pack) return null;

  const stripePromise = getStripe();

  if (!stripePromise) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl p-6">
            <p className="text-error-600">Stripe is not configured. Please contact support.</p>
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe' as const,
      variables: {
        colorPrimary: '#45a68f',
        colorText: '#1f2937',
        fontFamily: 'Avenir, system-ui, sans-serif',
        borderRadius: '8px',
      },
    },
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">Purchase Credits</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            <Elements stripe={stripePromise} options={options}>
              <CheckoutForm onSuccess={onSuccess} onCancel={onClose} pack={pack} />
            </Elements>
          </div>
        </div>
      </div>
    </div>
  );
};

